import { Calendar, DollarSign, CheckCircle2, Clock, ToggleRight, Download } from "lucide-react";

const sessions = [
  { time: "19:00", end: "21:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", platform: "TikTok", status: "CONFIRMED", confirmed: true },
  { time: "16:00", end: "17:30", title: "Flash Sale", brand: "Wardah", studio: "Studio C", platform: "Shopee", status: "OPEN SLOT", confirmed: false },
];

const payouts = [
  { amount: "Rp 12.800.000", date: "Mar 30", sessions: 2 },
  { amount: "Rp 6.400.000", date: "Mar 27", sessions: 1 },
];

const platformColor: Record<string, string> = {
  TikTok: "bg-destructive/10 text-destructive",
  Shopee: "bg-warning/10 text-warning",
};

export function HostPortalMockup() {
  return (
    <div className="border border-border bg-card/50 rounded-lg overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
        {/* Schedule panel */}
        <div className="p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-[12px] uppercase tracking-[0.12em] font-medium text-muted-foreground">My Sessions</span>
            <span className="ml-auto text-[11px] text-success font-medium">2 booked</span>
          </div>

          {/* Week strip */}
          <div className="flex gap-1 mb-5">
            {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day, i) => {
              const isToday = i === 2;
              const dates = [30, 31, 1, 2, 3, 4, 5];
              const dots = [0, 2, 2, 1, 2, 2, 1];
              return (
                <div
                  key={day}
                  className={`flex-1 text-center py-2 rounded ${isToday ? "border border-foreground/20 bg-muted/30" : ""}`}
                >
                  <div className="text-[9px] text-muted-foreground/60 uppercase tracking-wider">{day}</div>
                  <div className={`text-[14px] font-medium mt-0.5 ${isToday ? "text-foreground" : "text-foreground/70"}`}>{dates[i]}</div>
                  {dots[i] > 0 && (
                    <div className="flex justify-center gap-0.5 mt-1">
                      {Array.from({ length: Math.min(dots[i], 2) }).map((_, j) => (
                        <div key={j} className={`h-1 w-1 rounded-full ${j === 0 ? "bg-success" : "bg-warning"}`} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Session cards */}
          <div className="space-y-2.5">
            {sessions.map((s) => (
              <div key={s.time + s.title} className="border border-border rounded-lg p-3.5">
              <div className={`border-t-2 ${s.confirmed ? "border-success" : "border-warning"} -mt-3.5 -mx-3.5 mb-3 rounded-t-lg`} />
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[18px] font-semibold text-foreground">{s.time}</span>
                    <span className="text-[12px] text-muted-foreground ml-1.5">{s.end}</span>
                  </div>
                  {s.confirmed ? (
                    <span className="text-[10px] font-medium text-success bg-success/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-success" />
                      {s.status}
                    </span>
                  ) : (
                    <button className="text-[10px] font-medium text-warning bg-warning/10 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-warning/20 transition-colors">
                      <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                      {s.status}
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  <div className="text-[13px] font-medium text-foreground">{s.title}</div>
                  <div className="text-[11px] text-muted-foreground">{s.brand}</div>
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{s.studio}</span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${platformColor[s.platform]}`}>
                    {s.platform}
                  </span>
                </div>
                {!s.confirmed && (
                  <button className="mt-3 w-full text-[11px] font-medium text-warning border border-warning/30 bg-warning/5 hover:bg-warning/10 transition-colors rounded-md py-1.5">
                    Take this slot →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Availability panel */}
        <div className="p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-[12px] uppercase tracking-[0.12em] font-medium text-muted-foreground">Availability</span>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="border border-border rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground/60">Weekly hrs</div>
              <div className="text-[16px] font-semibold text-foreground mt-0.5">49h <span className="text-[10px] font-normal text-muted-foreground">/ 40h</span></div>
            </div>
            <div className="border border-border rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground/60">Active days</div>
              <div className="text-[16px] font-semibold text-foreground mt-0.5">5 <span className="text-[10px] font-normal text-muted-foreground">/ 7</span></div>
            </div>
            <div className="border border-border rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground/60">Shift blocks</div>
              <div className="text-[16px] font-semibold text-foreground mt-0.5">14</div>
            </div>
          </div>

          {/* Simple day list */}
          <div className="space-y-1.5">
            {[
              { day: "Mon", hours: "Off", sick: true },
              { day: "Tue", hours: "12h", sick: false },
              { day: "Wed", hours: "10h", sick: false },
              { day: "Thu", hours: "10h", sick: false },
              { day: "Fri", hours: "10h", sick: false },
              { day: "Sat", hours: "7h", sick: false },
              { day: "Sun", hours: "Off", sick: false },
            ].map((d) => (
              <div key={d.day} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border">
                <div className="flex items-center gap-2.5">
                  <div className={`h-2 w-2 rounded-full ${d.sick ? "bg-destructive" : d.hours === "Off" ? "bg-muted" : "bg-success"}`} />
                  <span className="text-[12px] text-foreground">{d.day}</span>
                  {d.sick && <span className="text-[9px] font-medium text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">SICK</span>}
                </div>
                <span className="text-[11px] text-muted-foreground">{d.hours}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payouts panel */}
        <div className="p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="text-[12px] uppercase tracking-[0.12em] font-medium text-muted-foreground">Payouts</span>
          </div>

          {/* Monthly cards */}
          <div className="space-y-2 mb-5">
            {[
              { month: "Apr 2026", amount: "Rp 63.9M", sessions: 9, current: true },
              { month: "Mar 2026", amount: "Rp 47.2M", sessions: 14, current: false },
              { month: "Feb 2026", amount: "Rp 36.4M", sessions: 11, current: false },
            ].map((m) => (
              <div key={m.month} className="border border-border rounded-lg p-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground">{m.month}</span>
                  {m.current && (
                    <span className="text-[8px] text-success bg-success/10 px-1 py-px rounded">Current</span>
                  )}
                </div>
                <div className="text-[14px] font-semibold text-foreground mt-1">{m.amount}</div>
                <div className="mt-2 h-1 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-success/80 rounded-full" style={{ width: m.current ? "100%" : m.sessions > 12 ? "75%" : "55%" }} />
                </div>
                <div className="text-[9px] text-muted-foreground mt-1">{m.sessions} sessions</div>
              </div>
            ))}
          </div>

          {/* Payout history */}
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2.5">Payout history</div>
          <div className="space-y-1.5">
            {payouts.map((p) => (
              <div key={p.date} className="flex items-center gap-3 border border-border rounded-lg px-3.5 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-foreground">{p.amount}</div>
                  <div className="text-[10px] text-muted-foreground">{p.date} · {p.sessions} session{p.sessions > 1 ? "s" : ""}</div>
                </div>
                <span className="text-[10px] text-muted-foreground border border-border px-2 py-0.5 rounded-full">Paid</span>
                <button className="text-muted-foreground/50 hover:text-foreground transition-colors ml-1">
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
